<template>

    <v-container fluid v-if="sessionActive">

        <v-card>

            <material-header
                title="Recruitment sources"
                info="Keep track of recruitment sources and costs"
                help="recruitment-sources"
                icon="mdi-magnet-on">
                <template slot="buttons">

                    <!-- Reload -->
                    <v-tooltip top color="primary">
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission(minPermission)"
                                   @click="refreshClicked()"
                                   v-on="on"
                                   icon>
                                <v-icon>mdi-refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Reload data</span>
                    </v-tooltip>

                    <!-- ADD NEW -->
                    <v-tooltip :color="hasPermission('create recruitment sources') ? 'primary' : 'red'" top>
                        <template v-slot:activator="{ on }">
                            <v-btn :disabled="!hasPermission('create recruitment sources')"
                                   v-on="on"
                                   fab
                                   small
                                   class="elevation-0 ml-2"
                                   color="primary"
                                   @click="newClicked()">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </template>
                        <span v-if="hasPermission('create recruitment sources')">Create new source</span>
                        <span v-else>You are not authorized to create new recruitment sources</span>
                    </v-tooltip>

                </template>

            </material-header>

            <v-alert v-if="!hasPermission(minPermission)"
                     class="body-2"
                     type="error"
            >
                Your user account doesn't have sufficient permissions for this feature
            </v-alert>

            <v-row v-if="hasPermission(minPermission)">

                <v-col cols="4" offset="8">

                    <v-text-field
                        class="mr-4 mt-2 mb-2"
                        v-model="search"
                        prepend-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        solo
                        dense
                        background-color="grey darken-2"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row>

                <v-col cols="12">

                    <v-data-table
                        :headers="headers"
                        :items="recruitmentSources"
                        :search="search"
                        :loading="loading"
                        :sort-by="sortBy"
                        :sort-desc="sortDesc"
                        :footer-props="{itemsPerPageOptions: [10,25,50,100, -1]}"
                    >
                        <template v-slot:body="{ items }">
                            <tbody>

                            <!-- NO DATA INFO -->
                            <tr v-if="items.length===0 && !loading">
                                <td colspan="6">
                                    <v-alert
                                        color="primary"
                                        class="mt-4 body-2"
                                        icon="mdi-information">
                                        No recruitment sources found
                                    </v-alert>
                                </td>
                            </tr>

                            <!-- ITEMS -->
                            <tr v-for="item in items" :key="item.name">

                                <!-- ID -->
                                <td>
                                    <v-chip :color="item.enabled ? 'primary' : 'grey darken-2'">{{ item.id }}</v-chip>
                                </td>

                                <!-- NAME -->
                                <td @click.stop="editClicked(item)" class="cursor-pointer">
                                    <v-tooltip open-delay="250" max-width="220" top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">{{ item.name }}</span>
                                        </template>
                                        <span>
                                            Click to edit
                                        </span>
                                    </v-tooltip>

                                    <v-tooltip open-delay="250" max-width="600" top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-show="item.tune_enabled" v-on="on" size="20" color="primary"
                                                    class="ml-1">
                                                mdi-alpha-t-circle-outline
                                            </v-icon>
                                        </template>
                                        <div>
                                            TUNE conversion reporting is turned on for this recruitment source
                                        </div>
                                        {{ item.tune_server_address }}
                                    </v-tooltip>
                                </td>

                                <!-- COST PER CONVERSION -->
                                <td class="text-right">
                                    <div class="mr-5">
                                        {{ accountingCurrencyPrefix }}
                                        {{ convertToAccountingCurrency(item.cost_per_conversion, true) }}
                                        {{ accountingCurrencyPostfix }}
                                    </div>
                                </td>

                                <!-- ENABLED -->
                                <td>
                                    <v-switch
                                        v-show="item.id !== 1"
                                        class="pt-0 mt-0"
                                        @click.capture.stop=""
                                        :disabled="!hasPermission('update recruitment sources') || item.id === 1"
                                        hide-details
                                        color="primary"
                                        ripple
                                        v-model="item.enabled"
                                        @change="toggleClicked(item)"
                                    ></v-switch>
                                </td>

                                <!-- ID -->
                                <td>
                                    <avatar v-if="item.created_by"
                                            :image-url="item.created_by.photo_url"
                                            :person-name="item.created_by.name"></avatar>
                                </td>

                                <td>
                                    {{ toHumanFrom(item.created_at) }}
                                </td>

                                <!-- ACTIONS -->
                                <td class="text-right">

                                    <div style="white-space: nowrap;">

                                        <v-tooltip color="primary" top>
                                            <template v-slot:activator="{ on }">
                                                <v-btn :disabled="!hasPermission('delete recruitment sources')"
                                                       icon
                                                       text
                                                       class="ma-0"
                                                       v-on="on"
                                                       @click="deleteClicked(item.id)"
                                                       color="secondary">
                                                    <v-icon light small color="white">mdi-delete</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Delete source</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                            </tr>
                            </tbody>
                        </template>

                    </v-data-table>

                </v-col>
            </v-row>

            <edit-recruitment-source-dialog
                v-model="editDialogModel"
                :source="sourceUnderEdit"
            ></edit-recruitment-source-dialog>

        </v-card>
    </v-container>

</template>

<script>

import Avatar from '../../../components/UI/Avatar';
import Store from '../store';
import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {progress} from '../../../components/mixins/progressMixin';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import VueJsonPretty from 'vue-json-pretty';
import Form from '../../../components/forms/Form';
import {currencyMixin} from "../../../components/mixins/currencyMixin";

import EditRecruitmentSourceDialog from './EditRecruitmentSourceDialog';

const vuexModule = 'RecruitmentSources';

export default {

    mixins: [activeSessionChecker, progress, timeDisplay, currencyMixin],

    components: {
        MaterialHeader,
        EditRecruitmentSourceDialog,
        VueJsonPretty,
        Avatar,
    },

    data() {
        return {
            debug: false,
            minPermission: 'view recruitment sources',
            editDialogModel: false,
            sourceUnderEdit: {},
            search: '',
            sortBy: 'name',
            sortDesc: false,
            headers: [
                {text: 'ID', align: 'center', sortable: true, value: 'id', width: '70px'},
                {text: 'NAME', align: 'left', sortable: true, value: 'name'},
                {text: 'COST', align: 'right', value: 'cost_per_conversion', width: '120px'},
                {text: 'ENABLED', value: 'enabled', width: '140px'},
                {text: 'CREATED BY', align: 'left', sortable: false, value: 'created_by', width: '120px'},
                {text: 'CREATED', align: 'left', sortable: true, value: 'created_at', width: '200px'},
                {text: 'ACTIONS', sortable: false, align: 'right', width: '40px'},
            ],
        }
    },
    computed: {

        ...mapState(vuexModule, [
            'loading',
            'recruitmentSources',
            'totalConversions'
        ]),
    },

    methods: {
        ...mapActions(vuexModule, [
            'loadRecruitmentSources',
            'delete'
        ]),

        ...mapMutations(vuexModule, []),

        percent(count) {
            return count / this.totalConversions * 100;
        },

        newClicked() {
            this.sourceUnderEdit = null;
            this.editDialogModel = true;
        },

        editClicked(item) {
            if (this.hasPermission('update recruitment sources')) {
                this.sourceUnderEdit = item;
                this.editDialogModel = true;
            }
        },

        refreshClicked() {
            this.loadRecruitmentSources(true);
        },

        toggleClicked(item) {

            let form = new Form(item, [
                'id',
                'name',
                'cost_per_conversion',
                'enabled',
                'tune_enabled',
                'tune_server_address'
            ]);

            this.$store.dispatch(vuexModule + '/update', form)
                .then(() => {
                })
                .catch((error) => {
                    if (error.status === 422) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Form contains errors!'
                        });
                    } else {
                        Bus.$emit('server-error');
                    }
                });
        },

        deleteClicked(id) {

            this.delete(id)
                .then(response => {
                    Bus.$emit('flash-message', {message: 'Recruitment source deleted'});
                })
                .catch(error => {

                    if (error.response.status === 409) {
                        Bus.$emit('flash-message', {type: 'error', message: error.response.data.message});
                    } else {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'OOPS! We failed to delete recruitment source!'
                        });
                    }
                });
        },
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }

        this.loadRecruitmentSources();
    }
}

</script>
